/*
 * Hydrates images that ship with their real URLs in data-src / data-srcset.
 *
 * Why this exists: loading="lazy" is not a budget. On a throttled connection Chrome
 * still fetches every image within ~3000px of the viewport, so the homepage was pulling
 * 170-245 images at once and starving the hero image of bandwidth (LCP 18s). Cards below
 * the fold now carry a 1x1 placeholder and are hydrated only once they approach the
 * viewport, which makes "load what the customer can see" deterministic instead of
 * dependent on a browser heuristic.
 *
 * Hover images are hydrated only on devices that can actually hover, so touch devices
 * never pay for them.
 */
(function () {
  var MAIN = 'img.card__img--deferred';
  var HOVER = 'img.card__img--deferred-hover';
  var canHover = window.matchMedia && window.matchMedia('(hover: hover)').matches;

  function hydrate(img) {
    var srcset = img.getAttribute('data-srcset');
    var src = img.getAttribute('data-src');
    if (srcset) img.setAttribute('srcset', srcset);
    if (src) img.setAttribute('src', src);
    img.removeAttribute('data-srcset');
    img.removeAttribute('data-src');
    img.classList.remove('card__img--deferred');
    img.classList.remove('card__img--deferred-hover');
  }

  // No IntersectionObserver (very old browser): hydrate everything rather than show blanks.
  if (!('IntersectionObserver' in window)) {
    document.querySelectorAll(MAIN + ',' + HOVER).forEach(hydrate);
    return;
  }

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var target = entry.target;
        observer.unobserve(target);

        // MOOGCO 2026-10-20: a horizontal product rail is observed as one unit. Cards that sit
        // off to the side inside the rail are clipped by its overflow, so they never "intersect"
        // until swiped into view and showed up blank for a moment. Hydrate the whole rail
        // (6-12 cards) as soon as the rail itself approaches the viewport.
        if (!target.matches(MAIN + ',' + HOVER)) {
          target.__mgHydrated = true;
          target.querySelectorAll(MAIN).forEach(hydrate);
          if (canHover) target.querySelectorAll(HOVER).forEach(hydrate);
          return;
        }

        var img = target;
        hydrate(img);

        // Bring the matching hover image in with its card, but only where hover exists.
        if (canHover) {
          var card = img.closest('.card__picture') || img.parentNode;
          if (card) {
            var hover = card.querySelector(HOVER);
            if (hover) hydrate(hover);
          }
        }
      });
    },
    // Start fetching shortly before the card scrolls in so it is rarely visibly empty.
    { rootMargin: '600px 0px' }
  );

  var RAIL = '.swiper, .wt-slider__container';

  // Cards inside a rail are watched through the rail (see above); everything else per image.
  function watchMain(img) {
    var rail = img.closest(RAIL);
    if (rail) {
      if (rail.__mgHydrated) { hydrate(img); return; } // card parsed after its rail loaded
      if (!rail.__mgObserved) { rail.__mgObserved = true; observer.observe(rail); }
      return;
    }
    observer.observe(img);
  }

  function watchHover(img) {
    var rail = img.closest(RAIL);
    if (rail) {
      if (rail.__mgHydrated) { hydrate(img); return; }
      if (!rail.__mgObserved) { rail.__mgObserved = true; observer.observe(rail); }
      return;
    }
    observer.observe(img);
  }

  function scan(root) {
    var scope = root || document;
    scope.querySelectorAll(MAIN).forEach(watchMain);
    // Above-the-fold cards keep a real src on the main image, so it is never
    // deferred and never hydrates its hover sibling. Watch hover images directly
    // as well, or those cards show a broken image on hover. Touch devices skip
    // this entirely and keep the bandwidth saving.
    if (canHover) {
      scope.querySelectorAll(HOVER).forEach(watchHover);
    }
  }

  scan(document);

  // Some rails only get their slider structure from theme JS after parsing, so at the first scan
  // their cards had no rail ancestor and were watched one by one (off-screen ones then never load).
  // Re-scan once the page and sliders are up; hydrate() is idempotent, so double work is harmless.
  window.addEventListener('load', function () { scan(document); });
  setTimeout(function () { scan(document); }, 2500);

  // Cards also arrive from AJAX pagination, filters and quick-view, so watch for new ones.
  if ('MutationObserver' in window) {
    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches(MAIN)) watchMain(node);
          else if (canHover && node.matches && node.matches(HOVER)) watchHover(node);
          else scan(node);
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  // Escape hatch for other scripts that inject cards imperatively.
  window.hydrateDeferredImages = scan;
})();
